/*
 * @Author: lizhigang
 * @Date: 2025-10-22 14:53:33
 * @Company: www.smm.cn
 * @Description: 注册
 */
"use client"

import IconFont from "@repo/ui/iconfont"
import { useLocale, useTranslations } from "next-intl"
import {
  AccountInput,
  composeCellphone,
  normalizePhoneNumber,
  resolveAccountType,
  useVerificationCode,
  type AccountInputValue,
} from "smm-auth-widget"
import { setAuthCookie } from "smm-auth-widget/core"
import { useRegister } from "smm-auth-widget/ui"
import { useCallback, useEffect, useState, type ReactNode } from "react"
import { Button, Form, Input, message, Space, type FormProps } from "antd"

import { usePasswordInput } from "../../_hooks"

import "./index.scss"

import { SIGN_IN_AUTH_CONFIG, SIGN_IN_EN_H5_SOURCE_MAP, SIGN_IN_EN_WEB_SOURCE_MAP } from "../../const"
import { isMobile } from "../../utils"
import { AuthConfigProvider } from "../AuthConfigProvider"
import { createPasswordValidator, PasswordRulesPopover } from "../PasswordRulesPopover"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onRegistered?: (msg?: string, phone?: { phoneCode: string; phoneNumber: string }) => void
  handleSignIn: () => void
}

interface VerificationCodeControlProps {
  value?: string
  onChange?: (value: string) => void
  isPhone: boolean
  placeholder: string
  sendButton: ReactNode
  switchLabel: string
  onPressEnter: () => void
  onToggleChannel: () => void
}

const VerificationCodeControl = ({
  value = "",
  onChange,
  isPhone,
  placeholder,
  sendButton,
  switchLabel,
  onPressEnter,
  onToggleChannel,
}: VerificationCodeControlProps) => (
  <>
    <Space.Compact block>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        onPressEnter={onPressEnter}
        allowClear
      />
      <span className="sendButton">{sendButton}</span>
    </Space.Compact>
    {isPhone && (
      <span className="verificationLabel">
        <button className="verifySwitchButton" type="button" onClick={onToggleChannel}>
          {switchLabel}
        </button>
      </span>
    )}
  </>
)

const Register = ({ onRegistered, handleSignIn, ...attr }: Props) => {
  const locale = useLocale()
  const t = useTranslations()
  const tr = (key: string) => t(`register.${key}`)
  const [form] = Form.useForm()
  const [account, setAccount] = useState<AccountInputValue>({ account: "", phoneCode: "" })
  const [accountHasError, setAccountHasError] = useState(true)
  const [verifyChannel, setVerifyChannel] = useState<"sms" | "whatsapp">("sms")
  const [password, setPassword] = useState("")
  const [hasPasswordError, setHasPasswordError] = useState(false)
  const [isH5, setIsH5] = useState(false)
  const [loading, registerApi] = useRegister()
  const [messageApi, contextHolder] = message.useMessage({
    getContainer: () => document.querySelector("#action") || document.body,
  })
  const verifyCode = Form.useWatch("verify_code", form) ?? ""

  const accountType = resolveAccountType(account.account, "auto")
  const cellphone = accountType === "phone" ? composeCellphone(account.phoneCode, account.account) : ""
  const isAccountValid = !accountHasError
  const { sendButton, requestCode } = useVerificationCode({
    type: accountType,
    action: "register",
    locale,
    params: accountType === "email" ? { email: account.account } : { cellphone, channel: verifyChannel },
    requestKey: accountType === "email" ? `email:${account.account}` : `phone:${cellphone}:${verifyChannel}`,
    disabled: !isAccountValid || loading,
    showError: (content) => messageApi.open({ type: "error", content }),
  })
  const handleToggleVerifyChannel = useCallback(() => {
    setVerifyChannel((channel) => (channel === "sms" ? "whatsapp" : "sms"))
    form.setFieldValue("verify_code", "")
  }, [form])
  const verifyLabel = t(verifyChannel === "sms" ? "signInCommon.verifyViaSms" : "signInCommon.verifyViaWhatsapp")
  const verifySwitchLabel = t(verifyChannel === "sms" ? "signInCommon.verifyViaWhatsapp" : "signInCommon.verifyViaSms")

  // 使用密码输入 Hook
  const { handlePasswordChange, handlePasswordPaste, handleCompositionStart } = usePasswordInput({
    form,
    fieldName: "password",
    onPasswordChange: setPassword,
  })

  const handleFieldsChange: NonNullable<FormProps["onFieldsChange"]> = (_changedFields, allFields) => {
    const passwordField = allFields.find((field) => field.name?.[0] === "password")
    setHasPasswordError((passwordField?.errors?.length ?? 0) > 0)
  }

  useEffect(() => {
    setIsH5(isMobile())
  }, [])

  /**
   * 注册
   */
  const handleSubmit = async () => {
    if (accountHasError) return
    const values = await form.validateFields()
    if (!values) return

    const commonValues = {
      password: values.password,
      verifyCode,
      locale,
      returnUrl: window.location.href,
      source: isMobile()
        ? (SIGN_IN_EN_H5_SOURCE_MAP[window.location.host] ?? "")
        : (SIGN_IN_EN_WEB_SOURCE_MAP[window.location.host] ?? ""),
    }

    await registerApi({
      values:
        accountType === "email"
          ? { ...commonValues, accountType, email: account.account }
          : { ...commonValues, accountType, cellphone },
      onSuccess: (response) => {
        const completeRegistration = () => {
          setAuthCookie(SIGN_IN_AUTH_CONFIG, response.data!.token, { temporary: true })
          onRegistered?.(
            tr("registerSuccess"),
            accountType === "phone"
              ? {
                  phoneCode: normalizePhoneNumber(account.phoneCode),
                  phoneNumber: normalizePhoneNumber(account.account),
                }
              : undefined
          )
        }

        messageApi.open({
          type: "success",
          content: tr("registerSuccess"),
          duration: 1,
          onClose: completeRegistration,
        })
      },
      onError: (msg) => {
        messageApi.open({
          type: "error",
          content: msg || "unknown error",
        })
      },
    })
  }

  return (
    <AuthConfigProvider>
      {contextHolder}
      <div className="registerWrapper page" {...attr}>
        <div className="logo">
          <IconFont type="icon-SMMlogo-daizimu" style={{ width: 80, height: 32, fontSize: 80 }} />
        </div>
        <h2 className="modalTitle page">{tr("title")}</h2>
        {/*<ConfigProvider*/}
        {/*  theme={{*/}
        {/*    components: {*/}
        {/*      Form: {*/}
        {/*        verticalLabelMargin: 40,*/}
        {/*      },*/}
        {/*    },*/}
        {/*  }}*/}
        {/*>*/}
        <Form
          layout="vertical"
          form={form}
          variant="filled"
          requiredMark={false}
          initialValues={{ variant: "borderless" }}
          className={"registerForm"}
          onFieldsChange={handleFieldsChange}
        >
          <div className="accountInputWrapperClass">
            <Form.Item label={null}>
              <AccountInput
                mode="auto"
                locale={locale}
                value={account}
                className="registerAccountInput"
                placeholder={t("accountInput.accountPlaceholder")}
                onEnter={() => void requestCode()}
                onChange={(value, hasError) => {
                  if (resolveAccountType(value.account, "auto") !== accountType) setVerifyChannel("sms")
                  setAccount(value)
                  setAccountHasError(hasError)
                }}
              />
            </Form.Item>
          </div>
          <Form.Item
            name="verify_code"
            className={accountType === "phone" ? "verificationCodeItem" : undefined}
            label={null}
            rules={[{ required: true, message: t("signInCommon.requireCode") }]}
          >
            <VerificationCodeControl
              isPhone={accountType === "phone"}
              placeholder={accountType === "email" ? tr("emailCode") : verifyLabel}
              sendButton={sendButton}
              switchLabel={verifySwitchLabel}
              onPressEnter={handleSubmit}
              onToggleChannel={handleToggleVerifyChannel}
            />
          </Form.Item>
          <Form.Item className="password-wrapper" style={{ position: "relative" }}>
            <Form.Item
              name="password"
              label={null}
              normalize={(value) => value?.trim()}
              rules={[{ required: true, message: tr("requirePassword") }, { validator: createPasswordValidator(t) }]}
              required={false}
            >
              <PasswordRulesPopover>
                <Input.Password
                  placeholder={tr("password")}
                  onChange={handlePasswordChange}
                  onPressEnter={handleSubmit}
                  onCompositionStart={handleCompositionStart}
                  onPaste={handlePasswordPaste}
                  allowClear
                />
              </PasswordRulesPopover>
            </Form.Item>
            {isH5 && !hasPasswordError && <div className="simplePasswordTip">{t("forgot.simplePasswordTip")}</div>}
          </Form.Item>
          <Form.Item className="submitButton">
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!isAccountValid || !password.trim() || !verifyCode.trim()}
              loading={loading}
              block
              danger
            >
              {tr("continue")}
            </Button>
          </Form.Item>
        </Form>
        {/*</ConfigProvider>*/}
        <div className={"bottomTip"}>
          {tr("hasAccount")}
          <a className={"text"} onClick={handleSignIn}>
            {tr("signIn")}
          </a>
        </div>
      </div>
    </AuthConfigProvider>
  )
}

export default Register
